import React from "react"
import { useLayout } from "contexts/components/LayoutContext"
import { ButtonElements } from "_types/Button"
import { Variant } from "./buttons-types"
import { CTA } from "./CTA"
import { Navigation } from "./Navigation"
import { OptionalCTAPhoneLink } from "./OptionalCTAPhoneLink"

interface ButtonParserProps {
  buttons: ButtonElements[]
  ctaColorVariantOutline?: Variant
  ctaColorVariantSolid?: Variant
  extraClassNames?: string
  isSingleCTAButtonAlwaysOutline?: boolean
  isSingleNavButtonAlwaysOutline?: boolean
  navigationColorVariantOutline?: Variant
  navigationColorVariantSolid?: Variant
  showOfficeNumberAfterButton?: boolean
  showIcon?: boolean
}

export const ButtonParser = ({
  buttons,
  showOfficeNumberAfterButton = false,
  showIcon = false,
  extraClassNames = "",
  ctaColorVariantSolid = "solid-primary",
  ctaColorVariantOutline = "outline-primary",
  navigationColorVariantSolid = "solid-primary",
  navigationColorVariantOutline = "outline-primary",
  isSingleNavButtonAlwaysOutline = false,
  isSingleCTAButtonAlwaysOutline = false,
}: ButtonParserProps) => {
  const { linkedPagesUrlPathCodex, slug: layoutSlug } = useLayout()

  const getCTAVariant = (index: number) => {
    if (isSingleCTAButtonAlwaysOutline && buttons.length === 1) {
      return ctaColorVariantOutline
    }

    const isFirst = index === 0

    return isFirst ? ctaColorVariantSolid : ctaColorVariantOutline
  }

  const getNavigationVariant = (index: number) => {
    if (isSingleNavButtonAlwaysOutline && buttons.length === 1) {
      return navigationColorVariantOutline
    }

    const isFirst = index === 0

    return isFirst ? navigationColorVariantSolid : navigationColorVariantOutline
  }

  return (
    <>
      {buttons.map((button, index) => {
        const {
          anchor_reference,
          associated_page,
          button_style,
          button_text,
          custom_anchor_reference,
          external_link,
          id,
          migrated_associated_page,
          use_main_office_call_tracking_number,
          custom_call_tracking_number,
        } = button

        const hasAnchorReference =
          !!custom_anchor_reference || !!anchor_reference

        const componentType = hasAnchorReference ? "anchor_button" : "button"

        const pageSlug =
          associated_page?.slug || migrated_associated_page || null
        const hasPageSlug = !!pageSlug

        const buttonSlug =
          hasAnchorReference && !hasPageSlug ? layoutSlug : pageSlug

        const isPhoneLink =
          use_main_office_call_tracking_number || !!custom_call_tracking_number

        const hasCTAFunctionality =
          hasAnchorReference || (isPhoneLink && !hasPageSlug)

        if (hasCTAFunctionality) {
          return (
            <CTA
              anchorReference={
                custom_anchor_reference || anchor_reference || "form-banner"
              }
              componentType={componentType}
              customPhoneNumber={custom_call_tracking_number ?? ""}
              extraClassNames={extraClassNames}
              usesMainOfficePhoneLink={use_main_office_call_tracking_number}
              index={index}
              key={`${id}-${index}`}
              label={button_text}
              onGetCTAVariant={
                button_style === "cta button"
                  ? getCTAVariant
                  : getNavigationVariant
              }
              showPhoneIcon={showIcon}
              slug={buttonSlug}
            />
          )
        }

        return (
          <Navigation
            externalLink={external_link}
            extraClassNames={extraClassNames}
            index={index}
            key={`${id}-${index}`}
            label={button_text}
            onGetNavigationVariant={
              button_style === "cta button"
                ? getCTAVariant
                : getNavigationVariant
            }
            slug={buttonSlug}
          />
        )
      })}
      {showOfficeNumberAfterButton && <OptionalCTAPhoneLink />}
    </>
  )
}
